import { EventDetailsInterface, UserSurveyInterface, UserSurveyOpenInterface } from '@elearning-platform/api-types/interfaces';
import { skipToken, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { getEventDetails, getQuestions } from '../api/requests';
import { QueryKey, ViewSearchParam } from '../enums';
import { UseEventFormDrawerData } from '../interfaces';
import { UseSearchParams } from '../types/common';
import { getFormInitialValues } from '../utils';

export const useEventFormDrawerData: () => UseEventFormDrawerData = (): UseEventFormDrawerData => {
  const [searchParams, setSearchParams]: UseSearchParams = useSearchParams();
  const eventId: string | null = searchParams.get(ViewSearchParam.TrainingId);

  const { data: event, isLoading: isEventLoading }: UseQueryResult<EventDetailsInterface> = useQuery({
    queryKey: [QueryKey.Event, eventId],
    queryFn: (): Promise<EventDetailsInterface> => getEventDetails(Number(eventId)),
    enabled: !!eventId,
  });
  const attemptId: number | null | undefined = event?.entrySurvey?.attemptId;

  const { data: surveyData, isFetching: isSurveyFetching }: UseQueryResult<AxiosResponse<UserSurveyInterface>> = useQuery({
    queryKey: [QueryKey.EnrollEventSurveyData, attemptId],
    queryFn: attemptId
      ? (): Promise<AxiosResponse<UserSurveyInterface>> => getQuestions(attemptId)
      : skipToken,
  });

  // TODO: extend guard - checking only for 'pages' presence isn't enough, validate its shape
  const isOpenSurvey: (survey: UserSurveyInterface) => survey is UserSurveyOpenInterface
  = (survey: UserSurveyInterface): survey is UserSurveyOpenInterface => 'pages' in survey;

  const openSurvey: UserSurveyOpenInterface | undefined = useMemo(
    (): UserSurveyOpenInterface | undefined => (!!surveyData && isOpenSurvey(surveyData.data)) ? surveyData.data : undefined,
    [surveyData]
  );

  const defaultValues: Record<string, string | boolean> = useMemo(
    (): Record<string, string | boolean> => (
      openSurvey ? getFormInitialValues(openSurvey) : {}
    ),
    [openSurvey]
  );

  return {
    eventData: { id: eventId, attemptId, details: event, isLoading: isEventLoading },
    drawerSettings: {
      isOpen: !!eventId,
      openDrawer: (trainingId: string): void => setSearchParams({ [ViewSearchParam.TrainingId]: trainingId }),
      closeDrawer: (): void => setSearchParams({}),
    },
    isFormLoading: !!attemptId && isSurveyFetching,
    survey: { enrollSurvey: openSurvey, defaultValues },
  };
};
